"use client"

import clsx from "clsx"
import React, { JSX, useCallback, useEffect, useState } from "react"
import { TCheck } from "./TCheck"
import { TRadioItem } from "./types"

/**
 * @description 다중 체크박스 그룹. 전체 선택(withAll) 옵션 지원
 * @example
 * <TMultiCheck
 *   items={[{ value: "a", label: "A" }, { value: "b", label: "B" }]}
 *   value={["a"]}
 *   withAll
 *   onChange={setSelected}
 * />
 */
interface TMultiCheckProps<V extends string | number> {
	/** 컨테이너 요소 id */
	id?: string
	/** 추가 CSS 클래스 */
	className?: string
	/** 체크박스 항목 배열. { value, label, disabled?, prefix?, suffix? } 형태 */
	items: Array<TRadioItem<V>>
	/** 현재 선택된 값 배열 */
	value?: Array<V>
	/** 선택 값 변경 콜백 */
	onChange: (value: Array<V>) => void
	/** 그룹 제목 */
	title?: string
	/**
	 * 가로 배치 여부
	 * @ai-style true=체크박스를 가로로 나열, false=세로로 나열
	 */
	row?: boolean
	/**
	 * "전체 선택" 체크박스 표시 여부
	 * @ai-style true=상단에 전체 선택 체크박스 추가, indeterminate 상태 자동 관리
	 * @default false
	 */
	withAll?: boolean
	/**
	 * "전체 선택" 체크박스 라벨
	 * @default "All"
	 */
	allLabel?: React.ReactNode
	/** "전체 선택" 체크박스의 data-* 속성 */
	allDataAttributes?: Record<string, string | number | boolean>
	/** 전체 비활성화 */
	disabled?: boolean
}

export const TMultiCheck = React.memo(<V extends string | number>(props: TMultiCheckProps<V>): JSX.Element | null => {
	const { id, className, title, row, withAll = false, allLabel = "All", allDataAttributes, disabled = false, items, value, onChange } = props

	const [selectedValue, setSelectedValue] = useState<V[]>([])

	useEffect(() => {
		setSelectedValue(value || [])
	}, [value])

	const onChangeValue = useCallback(
		(checked: boolean, targetValue: V) => {
			let newSelectedValues: V[]

			if (withAll && targetValue === ("all" as unknown as V)) {
				newSelectedValues = checked ? items.filter((item) => !item.disabled).map((item) => item.value) : []
			} else {
				if (checked) {
					newSelectedValues = [...selectedValue, targetValue]
				} else {
					newSelectedValues = selectedValue.filter((val) => val !== targetValue)
				}
			}
			setSelectedValue(newSelectedValues)
			onChange(newSelectedValues)
		},
		[selectedValue, withAll, items, onChange]
	)

	const enabledItemsValues = React.useMemo(() => items.filter((item) => !item.disabled).map((item) => item.value), [items])
	const isAllChecked = React.useMemo(
		() => withAll && enabledItemsValues.length > 0 && enabledItemsValues.every((val) => selectedValue.includes(val)),
		[withAll, enabledItemsValues, selectedValue]
	)
	const isIndeterminate = React.useMemo(() => withAll && selectedValue.length > 0 && !isAllChecked, [withAll, selectedValue, isAllChecked])

	if (items.length === 0 && !withAll) {
		return null
	}

	return (
		<div id={id} className={clsx("TMultiCheck", className)}>
			{title && <label className="title">{title}</label>}
			<div className={clsx("group", { row })}>
				{withAll && (
					<article>
						<TCheck
							checked={isAllChecked}
							indeterminate={isIndeterminate}
							disabled={disabled}
							label={allLabel}
							dataAttributes={allDataAttributes}
							onChange={(checked) => onChangeValue(checked, "all" as unknown as V)}
						/>
					</article>
				)}
				{items.map((item) => {
					const itemDisabled = item.disabled || disabled
					return (
						<article key={String(item.value)}>
							<TCheck
								checked={selectedValue.includes(item.value)}
								disabled={itemDisabled}
								dataAttributes={item.dataAttributes}
								onChange={(checked) => onChangeValue(checked, item.value)}
								label={
									<>
										{item.prefix && <span className="mr-1">{item.prefix}</span>}
										<span>{item.label}</span>
										{item.suffix && <span className="ml-1">{item.suffix}</span>}
									</>
								}
							/>
						</article>
					)
				})}
			</div>
		</div>
	)
}) as (<V extends string | number>(props: TMultiCheckProps<V>) => JSX.Element | null) & { displayName?: string }

TMultiCheck.displayName = "TMultiCheck"
